import React, { createContext, useContext, useState, ReactNode } from 'react';

interface CreateTaskDialogData {
  ideaId: string;
  ideaName: string;
  ideaDescription: string;
  sprintId?: string;
  use2Y3XMembers?: boolean;
  sourceType?: 'growth_hacking' | '2y3x';
  affectingMetric?: string[];
}

interface CreateTaskDialogContextType {
  isOpen: boolean;
  dialogData: CreateTaskDialogData | null;
  openCreateTaskDialog: (data: CreateTaskDialogData) => void;
  closeCreateTaskDialog: () => void;
  onCreateTask?: (taskData: {
    name: string;
    description: string;
    assignedTo: string;
    images?: string[];
    phase_id?: string;
  }) => Promise<void>;
  setOnCreateTask: (callback: (taskData: {
    name: string;
    description: string;
    assignedTo: string;
    images?: string[];
    phase_id?: string;
  }) => Promise<void>) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

const CreateTaskDialogContext = createContext<CreateTaskDialogContextType | undefined>(undefined);

interface CreateTaskDialogProviderProps {
  children: ReactNode;
}

export const CreateTaskDialogProvider = ({ children }: CreateTaskDialogProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dialogData, setDialogData] = useState<CreateTaskDialogData | null>(null);
  const [onCreateTask, setOnCreateTaskCallback] = useState<((taskData: {
    name: string;
    description: string;
    assignedTo: string;
    images?: string[];
    phase_id?: string;
  }) => Promise<void>) | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const openCreateTaskDialog = (data: CreateTaskDialogData) => {
    setDialogData(data);
    setIsOpen(true);
  };

  const closeCreateTaskDialog = () => {
    setIsOpen(false);
    // Keep data for persistence when navigating
  };

  const setOnCreateTask = (callback: (taskData: {
    name: string;
    description: string;
    assignedTo: string;
    images?: string[];
    phase_id?: string;
  }) => Promise<void>) => {
    setOnCreateTaskCallback(() => callback);
  };

  const value: CreateTaskDialogContextType = {
    isOpen,
    dialogData,
    openCreateTaskDialog,
    closeCreateTaskDialog,
    onCreateTask,
    setOnCreateTask,
    isLoading,
    setIsLoading,
  };

  return (
    <CreateTaskDialogContext.Provider value={value}>
      {children}
    </CreateTaskDialogContext.Provider>
  );
};

export const useCreateTaskDialog = () => {
  const context = useContext(CreateTaskDialogContext);
  if (context === undefined) {
    throw new Error('useCreateTaskDialog must be used within a CreateTaskDialogProvider');
  }
  return context;
};
